/**
 * AtcManifestCacheService - localStorage persistence layer for ATC manifests
 * 
 * Following Clean Architecture principles:
 * - This is an infrastructure/gateway concern
 * - Handles persistence details without business logic
 * - Provides clear "stale but usable" state identification
 * - All failures are observable (logged)
 */

import { Logger } from '@/app/utils/logger';
import { AtcMainManifest, AtcStationManifest } from '@/types/AtcManifest';
import {
  AtcCachedManifest,
  AtcCacheStatus,
  AtcCacheTTLConfig,
} from '@/types/AtcCache';

const LOG_TAG = 'atc-manifest-cache';

// localStorage keys
const MAIN_MANIFEST_CACHE_KEY = 'atc-main-manifest-cache';
const STATION_MANIFEST_CACHE_PREFIX = 'atc-station-manifest-cache';

// Default TTL values
const DEFAULT_MAIN_MANIFEST_TTL = 10 * 60 * 1000; // 10 minutes
const DEFAULT_STATION_MANIFEST_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Service for persisting ATC manifest data to localStorage with TTL support.
 * 
 * This service acts as a caching gateway, providing:
 * - Persistent storage across browser sessions
 * - TTL-based cache freshness tracking
 * - Clear "stale but usable" state identification
 * - Observable failures via logging
 */
export class AtcManifestCacheService {
  private static config: AtcCacheTTLConfig = {
    mainManifestTTL: DEFAULT_MAIN_MANIFEST_TTL,
    stationManifestTTL: DEFAULT_STATION_MANIFEST_TTL,
  };

  /**
   * Configure TTL values for caching
   * @param config Partial configuration to override defaults
   */
  static configure(config: Partial<AtcCacheTTLConfig>): void {
    this.config = { ...this.config, ...config };
    Logger.debug(`Cache configured: mainTTL=${this.config.mainManifestTTL}ms, stationTTL=${this.config.stationManifestTTL}ms`, LOG_TAG);
  }

  /**
   * Get the current TTL configuration
   */
  static getConfig(): AtcCacheTTLConfig {
    return { ...this.config };
  }

  // ============================================
  // Main Manifest Cache Operations
  // ============================================

  /**
   * Get cached main manifest from localStorage
   * @returns Cached manifest with metadata, or null if not found/invalid
   */
  static getCachedMainManifest(): AtcCachedManifest<AtcMainManifest> | null {
    try {
      const stored = localStorage.getItem(MAIN_MANIFEST_CACHE_KEY);
      if (!stored) {
        Logger.debug('No cached main manifest found', LOG_TAG);
        return null;
      }

      const cached = JSON.parse(stored) as AtcCachedManifest<AtcMainManifest>;
      
      // Validate structure
      if (!this.isValidCachedManifest(cached) || !this.isValidMainManifest(cached.data)) {
        Logger.warn('Invalid cached main manifest structure, clearing', LOG_TAG);
        this.clearMainManifestCache();
        return null;
      }

      const age = Date.now() - cached.timestamp;
      const isStale = age > this.config.mainManifestTTL;
      
      Logger.debug(`Retrieved cached main manifest: age=${Math.round(age / 1000)}s, stale=${isStale}`, LOG_TAG);
      return cached;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error reading cached main manifest: ${message}`, LOG_TAG);
      return null;
    }
  }

  /**
   * Save main manifest to localStorage
   * @param data The manifest data to cache
   * @param etag Optional ETag from the server response
   */
  static setCachedMainManifest(data: AtcMainManifest, etag?: string): void {
    try {
      const cached: AtcCachedManifest<AtcMainManifest> = {
        data,
        timestamp: Date.now(),
        updatedAt: data.updated_at,
        etag,
      };

      localStorage.setItem(MAIN_MANIFEST_CACHE_KEY, JSON.stringify(cached));
      Logger.info(`Cached main manifest with ${data.station_count} stations`, LOG_TAG);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error caching main manifest: ${message}`, LOG_TAG);
    }
  }

  /**
   * Clear the main manifest cache
   */
  static clearMainManifestCache(): void {
    try {
      localStorage.removeItem(MAIN_MANIFEST_CACHE_KEY);
      Logger.debug('Cleared main manifest cache', LOG_TAG);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error clearing main manifest cache: ${message}`, LOG_TAG);
    }
  }

  // ============================================
  // Station Manifest Cache Operations
  // ============================================

  /**
   * Generate localStorage key for a station manifest
   * @param manifestUrl The station manifest URL (used as unique identifier)
   */
  private static getStationCacheKey(manifestUrl: string): string {
    // Use URL hash to create a safe key
    const hash = this.hashString(manifestUrl);
    return `${STATION_MANIFEST_CACHE_PREFIX}-${hash}`;
  }

  /**
   * Get cached station manifest from localStorage
   * @param manifestUrl The station manifest URL
   * @returns Cached manifest with metadata, or null if not found/invalid
   */
  static getCachedStationManifest(manifestUrl: string): AtcCachedManifest<AtcStationManifest> | null {
    try {
      const key = this.getStationCacheKey(manifestUrl);
      const stored = localStorage.getItem(key);
      
      if (!stored) {
        Logger.debug(`No cached station manifest for ${manifestUrl}`, LOG_TAG);
        return null;
      }

      const cached = JSON.parse(stored) as AtcCachedManifest<AtcStationManifest>;
      
      // Validate structure
      if (!this.isValidCachedManifest(cached) || !this.isValidStationManifest(cached.data)) {
        Logger.warn(`Invalid cached station manifest structure for ${manifestUrl}, clearing`, LOG_TAG);
        this.clearStationManifestCache(manifestUrl);
        return null;
      }

      const age = Date.now() - cached.timestamp;
      const isStale = age > this.config.stationManifestTTL;
      
      Logger.debug(`Retrieved cached station manifest for ${cached.data.airport_code}/${cached.data.station_type}: age=${Math.round(age / 1000)}s, stale=${isStale}`, LOG_TAG);
      return cached;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error reading cached station manifest: ${message}`, LOG_TAG);
      return null;
    }
  }

  /**
   * Save station manifest to localStorage
   * @param manifestUrl The station manifest URL (used as unique identifier)
   * @param data The manifest data to cache
   * @param etag Optional ETag from the server response
   */
  static setCachedStationManifest(manifestUrl: string, data: AtcStationManifest, etag?: string): void {
    try {
      const cached: AtcCachedManifest<AtcStationManifest> = {
        data,
        timestamp: Date.now(),
        updatedAt: data.updated_at,
        etag,
      };

      const key = this.getStationCacheKey(manifestUrl);
      localStorage.setItem(key, JSON.stringify(cached));
      Logger.info(`Cached station manifest for ${data.airport_code}/${data.station_type} with ${data.scenario_count} scenarios`, LOG_TAG);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error caching station manifest: ${message}`, LOG_TAG);
    }
  }

  /**
   * Clear a specific station manifest cache
   * @param manifestUrl The station manifest URL
   */
  static clearStationManifestCache(manifestUrl: string): void {
    try {
      const key = this.getStationCacheKey(manifestUrl);
      localStorage.removeItem(key);
      Logger.debug(`Cleared station manifest cache for ${manifestUrl}`, LOG_TAG);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error clearing station manifest cache: ${message}`, LOG_TAG);
    }
  }

  // ============================================
  // Cache Status and Staleness Operations
  // ============================================

  /**
   * Check if the main manifest cache is stale
   * @returns true if stale or not cached, false if fresh
   */
  static isMainManifestStale(): boolean {
    const cached = this.getCachedMainManifest();
    if (!cached) {
      return true;
    }
    return Date.now() - cached.timestamp > this.config.mainManifestTTL;
  }

  /**
   * Check if a station manifest cache is stale
   * @param manifestUrl The station manifest URL
   * @returns true if stale or not cached, false if fresh
   */
  static isStationManifestStale(manifestUrl: string): boolean {
    const cached = this.getCachedStationManifest(manifestUrl);
    if (!cached) {
      return true;
    }
    return Date.now() - cached.timestamp > this.config.stationManifestTTL;
  }

  /**
   * Get detailed cache status for the main manifest
   */
  static getMainManifestCacheStatus(): AtcCacheStatus {
    const cached = this.getCachedMainManifest();

    if (!cached) {
      return {
        isCached: false,
        isStale: true,
        age: 0,
        source: 'none',
      };
    }

    const age = Date.now() - cached.timestamp;
    return {
      isCached: true,
      isStale: age > this.config.mainManifestTTL,
      age,
      source: 'cache',
    };
  }

  /**
   * Get detailed cache status for a station manifest
   * @param manifestUrl The station manifest URL
   */
  static getStationManifestCacheStatus(manifestUrl: string): AtcCacheStatus {
    const cached = this.getCachedStationManifest(manifestUrl);

    if (!cached) {
      return {
        isCached: false,
        isStale: true,
        age: 0,
        source: 'none',
      };
    }

    const age = Date.now() - cached.timestamp;
    return {
      isCached: true,
      isStale: age > this.config.stationManifestTTL,
      age,
      source: 'cache',
    };
  }

  /**
   * Alias for isMainManifestStale - general stale check
   */
  static isStale(manifestUrl?: string): boolean {
    if (manifestUrl) {
      return this.isStationManifestStale(manifestUrl);
    }
    return this.isMainManifestStale();
  }

  /**
   * Alias for cache status - returns status for main or station manifest
   */
  static getStaleStatus(manifestUrl?: string): AtcCacheStatus {
    if (manifestUrl) {
      return this.getStationManifestCacheStatus(manifestUrl);
    }
    return this.getMainManifestCacheStatus();
  }

  // ============================================
  // Cache Management
  // ============================================

  /**
   * Clear all ATC manifest caches (main + all stations)
   */
  static clearAllCache(): void {
    try {
      // Clear main manifest
      localStorage.removeItem(MAIN_MANIFEST_CACHE_KEY);

      // Find and clear all station manifest caches
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STATION_MANIFEST_CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      Logger.info(`Cleared all ATC manifest caches (main + ${keysToRemove.length} stations)`, LOG_TAG);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`Error clearing all caches: ${message}`, LOG_TAG);
    }
  }

  /**
   * Get count of cached station manifests (for debugging/testing)
   */
  static getCachedStationCount(): number {
    try {
      let count = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STATION_MANIFEST_CACHE_PREFIX)) {
          count++;
        }
      }
      return count;
    } catch {
      return 0;
    }
  }

  // ============================================
  // Private Helpers
  // ============================================

  /**
   * Simple string hash for generating cache keys from URLs
   */
  private static hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Validate that an object has the cached manifest structure
   */
  private static isValidCachedManifest(obj: unknown): obj is AtcCachedManifest<unknown> {
    if (!obj || typeof obj !== 'object') return false;
    const cached = obj as Record<string, unknown>;
    return (
      'data' in cached &&
      'timestamp' in cached &&
      typeof cached.timestamp === 'number' &&
      'updatedAt' in cached &&
      typeof cached.updatedAt === 'string'
    );
  }

  /**
   * Validate that an object has the main manifest structure
   */
  private static isValidMainManifest(obj: unknown): obj is AtcMainManifest {
    if (!obj || typeof obj !== 'object') return false;
    const manifest = obj as Record<string, unknown>;
    const hasStations = 'stations' in manifest && Array.isArray(manifest.stations);
    const hasAirports = 'airports' in manifest && Array.isArray(manifest.airports);

    return (
      (hasStations || hasAirports) &&
      'station_count' in manifest &&
      typeof manifest.station_count === 'number'
    );
  }

  /**
   * Validate that an object has the station manifest structure
   */
  private static isValidStationManifest(obj: unknown): obj is AtcStationManifest {
    if (!obj || typeof obj !== 'object') return false;
    const manifest = obj as Record<string, unknown>;
    return (
      'scenarios' in manifest &&
      Array.isArray(manifest.scenarios) &&
      'scenario_count' in manifest &&
      typeof manifest.scenario_count === 'number' &&
      'airport_code' in manifest &&
      'station_type' in manifest
    );
  }
}
